import React, { createContext, useContext, useState, useEffect } from 'react';

const UserContext = createContext();

const LOGIN_REDIRECT_DELAY_MS = 700;

const redirectToLogin = () => {
    setTimeout(() => {
        window.location.href = '/__catalyst/auth/login';
    }, LOGIN_REDIRECT_DELAY_MS);
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [deniedMessage, setDeniedMessage] = useState("");

    useEffect(() => {
        const checkAuth = async () => {
            console.log("Starting Auth Check...");
            try {
                const catalyst = window.catalyst;
                
                if (!catalyst || !catalyst.auth) {
                    console.error("Catalyst Auth SDK NOT found.");
                    // Fallback redirect if SDK fails to load
                    redirectToLogin();
                    return;
                }

                console.log("Catalyst SDK detected. Checking authentication...");

                // Initialize the Catalyst SDK with the project ID and zaid to avoid the 'null' app-name issue
                if (catalyst && typeof catalyst.initApp === 'function') {
                    catalyst.initApp({
                        project_Id: "23876000000036001",
                        zaid: "50042275978",
                        environment: "development"
                    });
                }

                // Wrap in try-catch to handle the 'net-issue' and 404s
                let isAuthResponse;
                try {
                    isAuthResponse = await catalyst.auth.isUserAuthenticated();
                    console.log("Auth Response:", isAuthResponse);
                } catch (sdkError) {
                    console.warn("SDK Auth check failed, likely due to session or local setup. Redirecting to login...");
                    redirectToLogin();
                    return;
                }

                if (isAuthResponse && isAuthResponse.content) {
                    console.log("User is authenticated. Fetching vendor details...");
                    const response = await fetch('/server/dealer_backend/user-detail');
                    const result = await response.json();

                    if (response.ok && result.status === "success") {
                        console.log("Vendor details loaded:", result.vendor);
                        setUser(result.vendor);
                    } else if (response.status === 401) {
                        // Backend disagrees with the client SDK about session validity
                        // (e.g. stale/local-dev session mismatch) - a real login redirect can fix this.
                        console.warn("Backend rejected session as unauthorized. Redirecting to login...");
                        redirectToLogin();
                    } else {
                        // Authenticated, but not registered as a vendor (or another non-auth failure).
                        // Re-logging in won't fix this, so surface it instead of dead-ending silently.
                        console.error("Backend failed to find vendor:", result.message);
                        setAccessDenied(true);
                        setDeniedMessage(
                            result.message ||
                            "You're signed in, but this account isn't registered as an Article Vendor in CRM."
                        );
                    }
                } else {
                    console.log("User NOT authenticated. Redirecting to login...");
                    redirectToLogin();
                }
            } catch (error) {
                console.error("Global Auth Error:", error);
                // Last resort fallback
                redirectToLogin();
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    const logout = () => {
        const catalyst = window.catalyst;
        if (catalyst && catalyst.auth) {
            const redirectURL = window.location.origin + '/__catalyst/auth/login';
            catalyst.auth.signOut(redirectURL);
        } else {
            window.location.href = '/__catalyst/auth/login';
        }
    };

    return (
        <UserContext.Provider value={{ user, loading, accessDenied, deniedMessage, logout }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
